import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { SEOHead } from '../../../components/seo/SEOHead';
import { LandingNav } from '../LandingNav';
import { LandingFooter } from '../LandingFooter';
import { useLandingTheme } from '../useLandingTheme';
import { FONT_MONO, FONT_SANS, FONT_SERIF, SUPPORT_EMAIL } from '../landingData';
import { useLandingLang, type LandingLang } from '../useLandingLang';
import { DOC_GROUPS } from './docsContent';
import type { DocBlock, DocImage, DocPage } from './docsTypes';
import '../landing.css';

const tw = (v: string) => ({ textWrap: v } as React.CSSProperties);
const eyebrow: React.CSSProperties = { fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--hv-leaf)' };

const ALL_PAGES: (DocPage & { group: string; groupTitle: Record<LandingLang, string> })[] = DOC_GROUPS.flatMap((g) =>
    g.pages.map((p) => ({ ...p, group: g.id, groupTitle: g.title })),
);
export const DOCS_FIRST_SLUG = ALL_PAGES[0].slug;

const UI = {
    en: { search: 'Search the docs…', onThisPage: 'On this page', prev: 'Previous', next: 'Next', menu: 'Docs menu', help: 'Still stuck? Email', noResults: 'No results for', beta: 'Beta', tip: 'Tip', note: 'Note', warning: 'Important', docs: 'Documentation' },
    id: { search: 'Cari di dokumentasi…', onThisPage: 'Di halaman ini', prev: 'Sebelumnya', next: 'Berikutnya', menu: 'Menu dokumentasi', help: 'Masih bingung? Email', noResults: 'Tidak ada hasil untuk', beta: 'Beta', tip: 'Tips', note: 'Catatan', warning: 'Penting', docs: 'Dokumentasi' },
} as const;

/* ------------------------------------------------------------------- page */

export const DocsPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const location = useLocation();
    const { user, isAuthenticated, logout } = useAuth();
    const [theme, toggleTheme] = useLandingTheme();
    const [lang, setLang] = useLandingLang();
    const [searchOpen, setSearchOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const ui = UI[lang];

    const index = ALL_PAGES.findIndex((p) => p.slug === slug);
    const page = ALL_PAGES[index];

    // Ctrl/⌘ K opens search from anywhere on the page.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // New page: top of the page, or the #section the link points at.
    useEffect(() => {
        setMenuOpen(false);
        const id = location.hash.slice(1);
        const el = id ? document.getElementById(id) : null;
        if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
        else window.scrollTo({ top: 0 });
    }, [slug, location.hash]);

    if (!page) return <Navigate to={`/docs/${DOCS_FIRST_SLUG}${location.search}`} replace />;

    const prev = ALL_PAGES[index - 1];
    const next = ALL_PAGES[index + 1];
    const headings = page.blocks.filter((b): b is Extract<DocBlock, { t: 'h2' }> => b.t === 'h2');

    return (
        <div className="hv-root" data-hv-theme={theme} lang={lang} style={{ position: 'static', fontFamily: FONT_SANS, color: 'var(--hv-ink)', background: 'var(--hv-page)', overflowX: 'clip', minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>
            <SEOHead
                title={`${page.title[lang]} · Docs`}
                description={page.description[lang]}
                url={`https://hyvay.com/docs/${page.slug}`}
                canonicalUrl={`https://hyvay.com/docs/${page.slug}`}
                keywords={['hyvay docs', 'hyvay documentation', 'panduan hyvay', page.title.en.toLowerCase()]}
            />
            <LandingNav theme={theme} onToggleTheme={toggleTheme} isAuthenticated={isAuthenticated} avatarUrl={user?.avatar} userName={user?.fullName} onLogout={logout} />

            <div className="hv-shell hv-docs" style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 28px 80px' }}>
                {/* Mobile: the sidebar folds into a menu button above the content. */}
                <div className="hv-docs-mobilebar" style={{ display: 'none', gap: 8, marginBottom: 18 }}>
                    <button type="button" onClick={() => setMenuOpen((v) => !v)} aria-expanded={menuOpen} className="hv-h-brand-bd" style={{ ...pillBtn, flex: 1, justifyContent: 'space-between' }}>
                        <span>{page.groupTitle[lang]} / {page.title[lang]}</span>
                        <span aria-hidden="true">{menuOpen ? '−' : '+'}</span>
                    </button>
                    <button type="button" onClick={() => setSearchOpen(true)} aria-label={ui.search} className="hv-h-brand-bd" style={pillBtn}>{SearchIcon}</button>
                </div>

                <div className="hv-docs-grid" style={{ display: 'grid', gridTemplateColumns: '236px minmax(0,1fr) 200px', gap: 48, alignItems: 'start' }}>
                    <aside className={`hv-docs-side${menuOpen ? ' is-open' : ''}`} aria-label={ui.menu} style={{ position: 'sticky', top: 20, maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', paddingBottom: 20 }}>
                        <button type="button" data-track="docs_search_open" onClick={() => setSearchOpen(true)} className="hv-h-brand-bd hv-docs-searchbtn" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 38, padding: '0 12px', borderRadius: 12, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', color: 'var(--hv-muted)', fontFamily: FONT_SANS, fontSize: 13, cursor: 'pointer' }}>
                            {SearchIcon}<span style={{ flex: 1, textAlign: 'left' }}>{ui.search}</span>
                            <kbd style={kbd}>⌘K</kbd>
                        </button>
                        <LangToggle lang={lang} onChange={setLang} />
                        <nav style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
                            {DOC_GROUPS.map((g) => (
                                <div key={g.id}>
                                    <div style={{ ...eyebrow, color: 'var(--hv-muted)', marginBottom: 6 }}>{g.title[lang]}</div>
                                    {g.pages.map((p) => {
                                        const active = p.slug === page.slug;
                                        return (
                                            <Link
                                                key={p.slug}
                                                to={`/docs/${p.slug}${location.search}`}
                                                aria-current={active ? 'page' : undefined}
                                                className={active ? undefined : 'hv-h-mint'}
                                                style={{ display: 'block', padding: '6px 10px', borderRadius: 8, fontSize: 13.5, lineHeight: 1.45, fontWeight: active ? 700 : 500, color: active ? 'var(--hv-mint-ink)' : 'var(--hv-body)', background: active ? 'var(--hv-mint)' : 'transparent', textDecoration: 'none' }}
                                            >
                                                {p.title[lang]}
                                                {p.beta && <span style={{ ...betaPill, marginLeft: 6, verticalAlign: 1 }}>{ui.beta}</span>}
                                            </Link>
                                        );
                                    })}
                                </div>
                            ))}
                        </nav>
                    </aside>

                    <main style={{ minWidth: 0, maxWidth: 780 }}>
                        <div style={eyebrow}>{page.groupTitle[lang]}</div>
                        <h1 style={{ margin: '12px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 52, lineHeight: 1.04, letterSpacing: '-0.02em', color: 'var(--hv-ink)', ...tw('balance') }} className="hv-docs-h1">
                            {page.title[lang]}
                            {page.beta && <span style={{ ...betaPill, marginLeft: 12, verticalAlign: 'middle', fontSize: 11 }}>{ui.beta}</span>}
                        </h1>
                        <p style={{ margin: '14px 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--hv-body)', ...tw('pretty') }}>{page.description[lang]}</p>

                        <div style={{ marginTop: 28 }}>
                            {page.blocks.map((b, i) => <Block key={i} block={b} lang={lang} search={location.search} />)}
                        </div>

                        {/* Prev / next */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 56 }}>
                            {prev ? <PrevNext to={`/docs/${prev.slug}${location.search}`} label={ui.prev} title={prev.title[lang]} /> : <span />}
                            {next ? <PrevNext to={`/docs/${next.slug}${location.search}`} label={ui.next} title={next.title[lang]} alignRight /> : <span />}
                        </div>
                        <p style={{ margin: '28px 0 0', fontSize: 13, color: 'var(--hv-muted)' }}>
                            {ui.help} <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--hv-mint-ink)', fontWeight: 700 }}>{SUPPORT_EMAIL}</a>
                        </p>
                    </main>

                    <Toc headings={headings} lang={lang} title={ui.onThisPage} slug={page.slug} />
                </div>
            </div>

            <LandingFooter />
            {searchOpen && <SearchDialog lang={lang} onClose={() => setSearchOpen(false)} search={location.search} />}
        </div>
    );
};

export default DocsPage;

/* ----------------------------------------------------------------- pieces */

const pillBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, height: 40, padding: '0 14px', borderRadius: 12, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontFamily: FONT_SANS, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const kbd: React.CSSProperties = { fontFamily: FONT_MONO, fontSize: 10, padding: '2px 6px', borderRadius: 6, border: '1px solid var(--hv-line)', color: 'var(--hv-muted)' };
const betaPill: React.CSSProperties = { display: 'inline-block', padding: '1px 7px', borderRadius: 999, background: 'var(--hv-sand)', color: 'var(--hv-ink)', fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 400 };

const SearchIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
);

const LangToggle: React.FC<{ lang: LandingLang; onChange: (l: LandingLang) => void }> = ({ lang, onChange }) => (
    <div role="radiogroup" aria-label="Language" style={{ display: 'flex', gap: 3, marginTop: 10, padding: 3, borderRadius: 10, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }}>
        {([['en', 'English'], ['id', 'Indonesia']] as [LandingLang, string][]).map(([code, label]) => (
            <button
                key={code}
                type="button"
                role="radio"
                aria-checked={lang === code}
                data-track="docs_language"
                data-track-label={code}
                onClick={() => onChange(code)}
                style={{ flex: 1, height: 28, borderRadius: 7, border: 0, cursor: 'pointer', fontFamily: FONT_SANS, fontSize: 12, fontWeight: 700, background: lang === code ? 'var(--hv-card)' : 'transparent', color: lang === code ? 'var(--hv-ink)' : 'var(--hv-muted)', boxShadow: lang === code ? '0 1px 2px var(--hv-shadow)' : undefined }}
            >
                {label}
            </button>
        ))}
    </div>
);

const PrevNext: React.FC<{ to: string; label: string; title: string; alignRight?: boolean }> = ({ to, label, title, alignRight }) => (
    <Link to={to} className="hv-h-brand-bd" style={{ display: 'block', padding: '14px 16px', borderRadius: 14, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', textAlign: alignRight ? 'right' : 'left', textDecoration: 'none' }}>
        <div style={{ fontSize: 12, color: 'var(--hv-muted)' }}>{alignRight ? `${label} →` : `← ${label}`}</div>
        <div style={{ marginTop: 3, fontSize: 14.5, fontWeight: 700, color: 'var(--hv-ink)' }}>{title}</div>
    </Link>
);

/** Right rail: h2s of the page, the one in view highlighted. */
const Toc: React.FC<{ headings: Extract<DocBlock, { t: 'h2' }>[]; lang: LandingLang; title: string; slug: string }> = ({ headings, lang, title, slug }) => {
    const [active, setActive] = useState<string | null>(null);
    useEffect(() => {
        setActive(headings[0]?.id ?? null);
        const els = headings.map((h) => document.getElementById(h.id)).filter(Boolean) as HTMLElement[];
        if (!els.length) return;
        const io = new IntersectionObserver(
            (entries) => {
                const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]) setActive(visible[0].target.id);
            },
            { rootMargin: '0px 0px -70% 0px' },
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [slug, headings]);

    if (!headings.length) return <div className="hv-docs-toc" />;
    return (
        <nav className="hv-docs-toc" aria-label={title} style={{ position: 'sticky', top: 20 }}>
            <div style={{ ...eyebrow, color: 'var(--hv-muted)' }}>{title}</div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--hv-line)' }}>
                {headings.map((h) => (
                    <a
                        key={h.id}
                        href={`#${h.id}`}
                        onClick={(e) => { e.preventDefault(); document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); history.replaceState(null, '', `#${h.id}`); setActive(h.id); }}
                        style={{ padding: '5px 0 5px 12px', marginLeft: -1, borderLeft: `2px solid ${active === h.id ? 'var(--hv-leaf)' : 'transparent'}`, fontSize: 12.5, lineHeight: 1.45, color: active === h.id ? 'var(--hv-ink)' : 'var(--hv-muted)', fontWeight: active === h.id ? 600 : 400, textDecoration: 'none' }}
                    >
                        {h.text[lang]}
                    </a>
                ))}
            </div>
        </nav>
    );
};

/* ----------------------------------------------------------------- blocks */

const Block: React.FC<{ block: DocBlock; lang: LandingLang; search: string }> = ({ block: b, lang, search }) => {
    switch (b.t) {
        case 'p':
            return <p style={{ margin: '0 0 16px', fontSize: 15.5, lineHeight: 1.75, color: 'var(--hv-body)', ...tw('pretty') }}><Inline text={b.text[lang]} search={search} /></p>;
        case 'h2':
            return (
                <h2 id={b.id} className="hv-docs-anchor" style={{ margin: '44px 0 14px', scrollMarginTop: 20, fontSize: 24, fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--hv-ink)' }}>
                    <a href={`#${b.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>{b.text[lang]}</a>
                </h2>
            );
        case 'h3':
            return <h3 style={{ margin: '28px 0 10px', fontSize: 17, fontWeight: 700, color: 'var(--hv-ink)' }}>{b.text[lang]}</h3>;
        case 'list': {
            const Tag = b.ordered ? 'ol' : 'ul';
            return (
                <Tag style={{ margin: '0 0 18px', paddingLeft: 22, display: 'grid', gap: 8, fontSize: 15.5, lineHeight: 1.7, color: 'var(--hv-body)' }}>
                    {b.items.map((it, i) => <li key={i}><Inline text={it[lang]} search={search} /></li>)}
                </Tag>
            );
        }
        case 'steps':
            return (
                <ol style={{ listStyle: 'none', margin: '8px 0 24px', padding: 0, display: 'grid', gap: 0 }}>
                    {b.items.map((s, i) => (
                        <li key={i} style={{ display: 'grid', gridTemplateColumns: '34px minmax(0,1fr)', gap: 14 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <span style={{ width: 30, height: 30, flex: 'none', borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800 }}>{i + 1}</span>
                                {i < b.items.length - 1 && <span aria-hidden="true" style={{ flex: 1, width: 2, margin: '6px 0', background: 'var(--hv-line)' }} />}
                            </div>
                            <div style={{ paddingBottom: i < b.items.length - 1 ? 26 : 0, minWidth: 0 }}>
                                <div style={{ marginTop: 4, fontSize: 16, fontWeight: 700, color: 'var(--hv-ink)' }}>{s.title[lang]}</div>
                                <p style={{ margin: '6px 0 0', fontSize: 15, lineHeight: 1.7, color: 'var(--hv-body)', ...tw('pretty') }}><Inline text={s.text[lang]} search={search} /></p>
                                {s.img && <Figure img={s.img} lang={lang} compact />}
                            </div>
                        </li>
                    ))}
                </ol>
            );
        case 'img':
            return <Figure img={b} lang={lang} />;
        case 'callout': {
            const tone = b.kind === 'warning'
                ? { bg: 'var(--hv-danger-bg)', fg: 'var(--hv-danger)' }
                : b.kind === 'tip'
                    ? { bg: 'var(--hv-mint)', fg: 'var(--hv-mint-ink)' }
                    : { bg: 'var(--hv-page-2)', fg: 'var(--hv-ink)' };
            return (
                <div role="note" style={{ display: 'flex', gap: 12, margin: '6px 0 20px', padding: '14px 16px', borderRadius: 14, background: tone.bg, color: tone.fg }}>
                    <span aria-hidden="true" style={{ flex: 'none', fontWeight: 800 }}>{b.kind === 'warning' ? '!' : b.kind === 'tip' ? '✦' : 'i'}</span>
                    <div style={{ fontSize: 14.5, lineHeight: 1.65 }}>
                        <b>{UI[lang][b.kind]}: </b><Inline text={b.text[lang]} search={search} />
                    </div>
                </div>
            );
        }
        case 'cards':
            return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px,100%), 1fr))', gap: 12, margin: '6px 0 24px' }}>
                    {b.items.map((c) => {
                        const target = ALL_PAGES.find((p) => p.slug === c.slug);
                        if (!target) return null;
                        return (
                            <Link key={c.slug} to={`/docs/${c.slug}${search}`} className="hv-h-lift" style={{ display: 'block', padding: 16, borderRadius: 14, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', textDecoration: 'none', transition: 'border-color 0.2s ease, transform 0.25s ease' }}>
                                <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hv-ink)' }}>{target.title[lang]} →</div>
                                <div style={{ marginTop: 4, fontSize: 13, lineHeight: 1.55, color: 'var(--hv-body)' }}>{c.text[lang]}</div>
                            </Link>
                        );
                    })}
                </div>
            );
        case 'table':
            return (
                <div style={{ overflowX: 'auto', margin: '6px 0 22px', borderRadius: 14, border: '1px solid var(--hv-line)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                        <thead>
                            <tr style={{ background: 'var(--hv-page-2)' }}>
                                {b.head.map((h, i) => <th key={i} style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 700, color: 'var(--hv-ink)' }}>{h[lang]}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {b.rows.map((r, i) => (
                                <tr key={i} style={{ borderTop: '1px solid var(--hv-line)' }}>
                                    {r.map((c, j) => <td key={j} style={{ padding: '10px 14px', lineHeight: 1.55, color: j ? 'var(--hv-body)' : 'var(--hv-ink)', verticalAlign: 'top' }}><Inline text={c[lang]} search={search} /></td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
    }
};

const Figure: React.FC<{ img: DocImage; lang: LandingLang; compact?: boolean }> = ({ img, lang, compact }) => (
    <figure style={{ margin: compact ? '14px 0 0' : '8px 0 26px' }}>
        <a href={`/images/docs/${img.src}`} target="_blank" rel="noreferrer" style={{ display: 'block', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--hv-line)', boxShadow: '0 24px 40px -32px var(--hv-shadow)', background: 'var(--hv-card)' }}>
            <img src={`/images/docs/${img.src}`} alt={img.alt[lang]} loading="lazy" style={{ display: 'block', width: '100%', height: 'auto' }} />
        </a>
        {img.caption && <figcaption style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.5, color: 'var(--hv-muted)', textAlign: 'center' }}>{img.caption[lang]}</figcaption>}
    </figure>
);

/** Renders **bold**, `code` and [label](href). Plain text only — never HTML. */
const Inline: React.FC<{ text: string; search: string }> = ({ text, search }) => {
    const parts: React.ReactNode[] = [];
    const re = /\*\*(.+?)\*\*|`(.+?)`|\[(.+?)\]\((.+?)\)/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        const k = parts.length;
        if (m[1]) parts.push(<b key={k} style={{ color: 'var(--hv-ink)' }}>{m[1]}</b>);
        else if (m[2]) parts.push(<code key={k} style={{ fontFamily: FONT_MONO, fontSize: '0.86em', padding: '1px 6px', borderRadius: 6, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)', color: 'var(--hv-ink)' }}>{m[2]}</code>);
        else if (m[4].startsWith('/')) {
            const [path, hash] = m[4].split('#');
            parts.push(<Link key={k} to={`${path}${path.startsWith('/docs/') ? search : ''}${hash ? `#${hash}` : ''}`} style={inlineLink}>{m[3]}</Link>);
        } else parts.push(<a key={k} href={m[4]} target={m[4].startsWith('http') ? '_blank' : undefined} rel="noreferrer" style={inlineLink}>{m[3]}</a>);
        last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return <>{parts}</>;
};
const inlineLink: React.CSSProperties = { color: 'var(--hv-mint-ink)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3 };

/* ----------------------------------------------------------------- search */

const plain = (s: string) => s.replace(/\*\*(.+?)\*\*/g, '$1').replace(/`(.+?)`/g, '$1').replace(/\[(.+?)\]\(.+?\)/g, '$1');

/** Searchable chunks: a page's title/lead, then each h2 section with its text. */
function buildIndex(lang: LandingLang) {
    const rows: { slug: string; hash?: string; page: string; section?: string; text: string }[] = [];
    for (const p of ALL_PAGES) {
        let cur = { slug: p.slug, hash: undefined as string | undefined, page: p.title[lang], section: undefined as string | undefined, text: plain(p.description[lang]) };
        rows.push(cur);
        for (const b of p.blocks) {
            if (b.t === 'h2') {
                cur = { slug: p.slug, hash: b.id, page: p.title[lang], section: b.text[lang], text: '' };
                rows.push(cur);
                continue;
            }
            const texts: string[] =
                b.t === 'p' || b.t === 'callout' || b.t === 'h3' ? [b.text[lang]]
                : b.t === 'list' ? b.items.map((i) => i[lang])
                : b.t === 'steps' ? b.items.flatMap((s) => [s.title[lang], s.text[lang]])
                : b.t === 'table' ? b.rows.flat().map((c) => c[lang])
                : [];
            cur.text += ' ' + texts.map(plain).join(' ');
        }
    }
    return rows;
}

const SearchDialog: React.FC<{ lang: LandingLang; onClose: () => void; search: string }> = ({ lang, onClose, search }) => {
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [sel, setSel] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const index = useMemo(() => buildIndex(lang), [lang]);
    const ui = UI[lang];

    const results = useMemo(() => {
        const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
        if (!terms.length) return [];
        return index
            .map((r) => {
                const hay = `${r.page} ${r.section ?? ''} ${r.text}`.toLowerCase();
                if (!terms.every((t) => hay.includes(t))) return null;
                const title = `${r.page} ${r.section ?? ''}`.toLowerCase();
                const score = terms.reduce((s, t) => s + (title.includes(t) ? 3 : 1), 0) + (r.section ? 0 : 0.5);
                const at = r.text.toLowerCase().indexOf(terms[0]);
                const snippet = at >= 0 ? (at > 40 ? '…' : '') + r.text.slice(Math.max(0, at - 40), at + 90).trim() + '…' : r.text.slice(0, 110);
                return { ...r, score, snippet };
            })
            .filter(Boolean)
            .sort((a, b) => b!.score - a!.score)
            .slice(0, 8) as (ReturnType<typeof buildIndex>[number] & { score: number; snippet: string })[];
    }, [q, index]);

    useEffect(() => { inputRef.current?.focus(); }, []);
    useEffect(() => { setSel(0); }, [q]);

    const go = (r: { slug: string; hash?: string }) => {
        onClose();
        navigate(`/docs/${r.slug}${search}${r.hash ? `#${r.hash}` : ''}`);
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(20,21,22,0.55)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '12vh 16px 16px' }}>
            <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={ui.search} style={{ width: '100%', maxWidth: 600, borderRadius: 18, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 50px 80px -40px rgba(0,0,0,0.55)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', borderBottom: '1px solid var(--hv-line)', color: 'var(--hv-muted)' }}>
                    {SearchIcon}
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') onClose();
                            else if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
                            else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
                            else if (e.key === 'Enter' && results[sel]) go(results[sel]);
                        }}
                        placeholder={ui.search}
                        aria-label={ui.search}
                        style={{ flex: 1, height: 52, border: 0, outline: 'none', background: 'transparent', fontFamily: FONT_SANS, fontSize: 15, color: 'var(--hv-ink)' }}
                    />
                    <kbd style={kbd}>Esc</kbd>
                </div>
                <div style={{ maxHeight: '55vh', overflowY: 'auto', padding: results.length || q ? 8 : 0 }}>
                    {q && !results.length && <p style={{ margin: 0, padding: 16, fontSize: 13.5, color: 'var(--hv-muted)' }}>{ui.noResults} “{q}”.</p>}
                    {results.map((r, i) => (
                        <button
                            key={`${r.slug}#${r.hash ?? ''}`}
                            type="button"
                            onMouseEnter={() => setSel(i)}
                            onClick={() => go(r)}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 10, border: 0, cursor: 'pointer', fontFamily: FONT_SANS, background: i === sel ? 'var(--hv-mint)' : 'transparent' }}
                        >
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--hv-ink)' }}>{r.page}{r.section ? <span style={{ fontWeight: 500, color: 'var(--hv-body)' }}> › {r.section}</span> : null}</div>
                            <div style={{ marginTop: 2, fontSize: 12.5, lineHeight: 1.5, color: 'var(--hv-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.snippet}</div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
